"use client";

import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";

import { Region, SearchService } from "@/services/search.service";
import { Filter } from "@/types/SearchFilter.types";
import { CloseButton } from "@/ui/CloseButton";
import { cn } from "@/utils/cn";
import { createLogger } from "@/utils/logger";

const logger = createLogger("recommended-cities");

interface RecommendedCitiesProps {
  selectedFilters: Filter;
  setSelectedFilters: React.Dispatch<React.SetStateAction<Filter>>;
  regionsListData: Region[] | null;
  closeWindow?: () => void;
  className?: string;
}

export const RecommendedCities: React.FC<RecommendedCitiesProps> = ({
  selectedFilters,
  setSelectedFilters,
  regionsListData,
  closeWindow,
  className,
}) => {
  const t = useTranslations();
  const locale = useLocale();

  const { data: recommendedCitiesData, error: recommendedCitiesError } =
    useQuery({
      queryKey: ["getRecommendedCities"],
      queryFn: () => SearchService.getRecommendedCities(),
    });

  const handleCityClick = (cityName: string) => {
    logger.info("City clicked:", cityName);

    if (!regionsListData) {
      logger.warn("No regions data available");
      return;
    }

    let parentRegion: Region | null = null;
    let foundCity = null;

    // Find city + region
    for (const region of regionsListData) {
      const city = region.cities?.find((c) => c.name.en === cityName);
      if (city) {
        parentRegion = region;
        foundCity = city;
        break;
      }
    }

    if (!parentRegion || !foundCity) {
      logger.warn(`Could not find region for city: ${cityName}`);
      return;
    }

    const regionName = parentRegion.name.en;
    const localizedCityName = foundCity.name.en;

    setSelectedFilters((prev) => {
      const prevSelectedCity = prev.city?.[0];
      const isSameCityClicked =
        prev.city?.length === 1 && prevSelectedCity === localizedCityName;

      // Pre-calc structure
      const districtsOfCity = foundCity.districts?.map((d) => d.name.en) ?? [];

      const microdistrictsOfCity =
        foundCity.districts?.flatMap(
          (d) => d.microdistricts?.map((m) => m.name.en) ?? [],
        ) ?? [];

      const streetsOfCity =
        foundCity.districts?.flatMap(
          (d) => d.streets?.map((s) => s.name.en) ?? [],
        ) ?? [];

      if (isSameCityClicked) {
        return {
          ...prev,
          region: [],
          city: [],
          district: [],
          microdistrict: [],
          street: [],
          metroStation: [],
        };
      }

      // Select ONLY this city
      return {
        ...prev,
        region: [regionName],
        city: [localizedCityName],
        district: [],
        microdistrict: [],
        street: [],
        metroStation: [],
      };
    });
  };

  if (recommendedCitiesError) {
    logger.error("Error loading recommended cities:", recommendedCitiesError);
    return null;
  }

  if (!recommendedCitiesData?.length) {
    return null;
  }

  return (
    <div className={cn("py-6 border-b border-gray-200 w-full", className)}>
      <div className="flex justify-between align-center border-b border-gray-200">
        <h3 className="m-0 !border-none">{t("Popular cities")}</h3>
        {closeWindow && (
          <div className={cn("mx-4 hidden md:grid items-center")}>
            <CloseButton
              size={25}
              className={"p-4"}
              onClick={closeWindow}
            />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-2 gap-y-6 px-4 pt-6 md:max-h-28 md:overflow-y-auto">
        {recommendedCitiesData
          .sort((a, b) => a.numberRecommendation - b.numberRecommendation)
          .map((city) => {
            const cityName = city.name.en || Object.values(city.name)[0];

            return (
              <div
                key={cityName}
                onClick={() => handleCityClick(cityName)}
                className={cn(
                  "inline-flex items-center px-3 py-2 text-medium rounded-lg cursor-pointer leading-5",
                  selectedFilters.city?.length === 1 &&
                    selectedFilters.city?.includes(cityName) &&
                    "font-medium text-red-700",
                  "sm:hover:text-red-700",
                  "focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
                  "transition-colors duration-200",
                )}
              >
                <span>{city.name[locale]}</span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default RecommendedCities;
